// ---------------------------------------------------------------------------
// The recycle bin: deleted campaigns, restore, delete for good (R18b, from pages/settings.ts)
//
// A deleted campaign stops sending and is kept for the server's retention
// period (30 days unless the server says otherwise), then purged with its
// leads and messages. The bin lists them, restores one — which never resumes
// sending on its own, so the toast says which state it came back in — or
// deletes one for good after a question that says what goes. A bin that did
// not load says so (R18a), never "No deleted campaigns".
// ---------------------------------------------------------------------------

import { confirmDialog } from '../confirm-dialog.js';
import { type Say, type Send, viaFetch } from './settings-flow.js';

/** One soft-deleted campaign, as served by GET /api/campaigns/deleted. */
export interface DeletedCampaign {
  _id: string;
  name: string;
  campaignType: string;
  /** The status it will be restored to (already normalised server-side). */
  statusBeforeDelete?: string;
  deletedAt: string;
  /** Computed server-side — a countdown stored in the document would be stale the next day. */
  daysLeft?: number;
  purgeAt?: string;
  leadsProcessed?: number;
  totalSent?: number;
}

/** What the bin holds, and for how long the server keeps it (null: the server did not say). */
export interface Bin {
  campaigns: DeletedCampaign[];
  retentionDays: number | null;
}

/** The bin, or null when it could not be loaded. Never throws. */
export async function loadBin(send: Send = viaFetch): Promise<Bin | null> {
  try {
    const resp = await send('/api/campaigns/deleted');
    const data = await resp.json();
    // Failed unless the server says ok.
    if (data.ok) {
      return {
        campaigns: data.campaigns ?? [],
        retentionDays: typeof data.retentionDays === 'number' ? data.retentionDays : null,
      };
    }
  } catch {
    // Non-fatal: the rest of Settings is unaffected. The section says it
    // could not load rather than "No deleted campaigns." (R18a).
  }
  return null;
}

export interface BinDeps {
  send?: Send;
  say: Say;
  /** The campaign with a request in flight ('' for none). */
  setBusy(campaignId: string): void;
  /** The campaign left the bin. */
  removed(campaignId: string): void;
  confirm?: typeof confirmDialog;
}

const titled = (c: DeletedCampaign) => c.name || 'Untitled';

/** Restores a campaign. A restored campaign never resumes sending on its own. */
export async function restoreCampaign(c: DeletedCampaign, deps: BinDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  deps.setBusy(c._id);
  try {
    const resp = await send(`/api/campaigns/${encodeURIComponent(c._id)}/restore`, { method: 'POST' });
    const data = await resp.json().catch(() => null);
    if (!resp.ok || !data?.ok) {
      deps.say.error(data?.error ?? 'Failed to restore campaign');
      return;
    }
    deps.removed(c._id);
    // Say which state it came back in so nobody assumes outreach just restarted.
    deps.say.success(
      data.status === 'paused'
        ? `“${titled(c)}” restored — it is paused, resume it when you want it sending again.`
        : `“${titled(c)}” restored to your dashboard.`,
    );
  } catch {
    deps.say.error('Failed to restore campaign (network error)');
  } finally {
    deps.setBusy('');
  }
}

/** Deletes a campaign for good, after a question that names what cannot be undone. */
export async function purgeCampaign(c: DeletedCampaign, deps: BinDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  const confirm = deps.confirm ?? confirmDialog;
  // The one irreversible action in this flow, so it says exactly what goes
  // and names what cannot be undone.
  if (
    !(await confirm(
      `Permanently delete “${titled(c)}”? This removes the campaign, its leads and every generated message for good. This cannot be undone.`,
      { title: 'Delete permanently', confirmLabel: 'Delete permanently', confirmClass: 'btn-error' },
    ))
  )
    return;
  deps.setBusy(c._id);
  try {
    const resp = await send(`/api/campaigns/${encodeURIComponent(c._id)}/permanent`, { method: 'DELETE' });
    const data = await resp.json().catch(() => null);
    if (!resp.ok || !data?.ok) {
      deps.say.error(data?.error ?? 'Failed to delete campaign');
      return;
    }
    deps.removed(c._id);
    deps.say.success('Campaign permanently deleted.');
    if (data.warning) deps.say.warning(data.warning);
  } catch {
    deps.say.error('Failed to delete campaign (network error)');
  } finally {
    deps.setBusy('');
  }
}
